import * as React from "react";

interface ShortcutOptions {
  key: string;
  meta?: boolean;
  ctrl?: boolean;
  shift?: boolean;
  preventDefault?: boolean;
  enabled?: boolean;
}

function isTypingContext(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable;
}

export function useKeyboardShortcut(options: ShortcutOptions, callback: () => void) {
  const { key, meta, ctrl, shift, preventDefault = true, enabled = true } = options;
  const callbackRef = React.useRef(callback);
  callbackRef.current = callback;

  React.useEffect(() => {
    if (!enabled) return;

    function handler(e: KeyboardEvent) {
      const matchesKey = e.key.toLowerCase() === key.toLowerCase();
      const matchesMeta = meta ? e.metaKey || e.ctrlKey : true;
      const matchesCtrl = ctrl ? e.ctrlKey : true;
      const matchesShift = shift ? e.shiftKey : true;

      if (!matchesKey || !matchesMeta || !matchesCtrl || !matchesShift) return;
      if (!meta && !ctrl && isTypingContext(e.target)) return;

      if (preventDefault) e.preventDefault();
      callbackRef.current();
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [key, meta, ctrl, shift, preventDefault, enabled]);
}
